import {ChangeDetectorRef, Component, inject, Injector, Input, OnChanges, OnDestroy} from '@angular/core';
import {firstValueFrom, timeout} from 'rxjs';
import {MasterThreadService} from '../../../services/objects/master-thread.service';
import {NgClass, NgStyle} from '@angular/common';
import {Marked} from 'marked';
import {hasLowerComparablePrice} from '../../../utils/product-card-price-comparison';

import {IChatbotOptions} from '../../../models/chatbot-options.model';
import {IAttachmentSummary, IChatHistory} from '../../../models/conversation.model';
import {
  buildConversationTranscriptSegments,
  ConversationProductCardsSegment,
  ConversationProductCardView,
  ConversationTranscriptSegment
} from './conversation-transcript-segments';
import {
  getConversationAttachmentDisplayName,
  getConversationAttachmentMetaText,
  readConversationAttachmentSummaries
} from './conversation-attachment-summary';

interface ProductCardPrice {
  current: string;
  original: string;
}

@Component({
  selector: 'app-conversation-transcript-renderer',
  standalone: true,
  imports: [NgClass, NgStyle],
  templateUrl: './conversation-transcript-renderer.component.html',
  styleUrls: ['./conversation-transcript-renderer.component.scss'],
  providers: [Marked]
})
export class ConversationTranscriptRendererComponent implements OnChanges, OnDestroy {
  private readonly injector = inject(Injector);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private attachmentGeneration = 0;
  attachmentUrls: Record<string, string> = {};
  attachmentStates: Record<string, string> = {};
  @Input() conversationId = '';
  private readonly markedService = inject(Marked);
  private readonly failedProductCardImages = new Set<string>();

  @Input() message?: IChatHistory;
  @Input() chatbot?: IChatbotOptions | null;

  segments: ConversationTranscriptSegment[] = [];
  attachmentSummaries: IAttachmentSummary[] = [];

  ngOnChanges(): void {
    this.releaseAttachments();
    this.failedProductCardImages.clear();
    this.segments = buildConversationTranscriptSegments(this.message, {
      fallbackPresentation: this.chatbot?.frontEndOptions?.productCardPresentation
    });
    this.attachmentSummaries = readConversationAttachmentSummaries(this.message);
  }

  ngOnDestroy(): void { this.releaseAttachments(); }

  private releaseAttachments(): void {
    this.attachmentGeneration++;
    Object.values(this.attachmentUrls).forEach(url => URL.revokeObjectURL(url));
    this.attachmentUrls = {};
    this.attachmentStates = {};
  }

  canPreviewAttachment(attachment: IAttachmentSummary): boolean {
    return !!this.conversationId && !!this.message?.messageId && !!attachment.attachmentId &&
      ['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(attachment.contentType || '');
  }

  async previewAttachment(attachment: IAttachmentSummary): Promise<void> {
    const id = attachment.attachmentId;
    if (!id || !this.canPreviewAttachment(attachment) || this.attachmentUrls[id] || this.attachmentStates[id] === 'loading') return;
    if (!attachment.expiresAtUtc || !Number.isFinite(Date.parse(attachment.expiresAtUtc)) || Date.parse(attachment.expiresAtUtc) <= Date.now()) {
      this.attachmentStates[id] = 'Image expired';
      return;
    }
    const generation = this.attachmentGeneration;
    this.attachmentStates[id] = 'loading';
    try {
      const blob = await firstValueFrom(this.injector.get(MasterThreadService)
        .getAttachment(this.conversationId, this.message!.messageId!, id).pipe(timeout(15000)));
      if (generation !== this.attachmentGeneration) return;
      if (blob.size > 4 * 1024 * 1024 || !['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(blob.type))
        throw new Error('Unsupported image');
      this.attachmentUrls[id] = URL.createObjectURL(blob);
      this.attachmentStates[id] = 'ready';
      this.changeDetector.markForCheck();
    } catch {
      if (generation === this.attachmentGeneration) {
        this.attachmentStates[id] = 'Image unavailable';
        this.changeDetector.markForCheck();
      }
    }
  }

  getMarkdownText(text: string): string {
    return this.markedService.parse(text || '') as string;
  }

  isUserMessage(): boolean {
    return this.message?.user === true;
  }

  isHumanMessage(): boolean {
    return this.message?.human === true;
  }

  getTextSegmentClasses(): Record<string, boolean> {
    return {
      'conversation-transcript-text--plain': this.isUserMessage(),
      'conversation-transcript-bubble': !this.isUserMessage(),
      'conversation-transcript-bubble--assistant': !this.isUserMessage() && !this.isHumanMessage(),
      'conversation-transcript-bubble--human': this.isHumanMessage()
    };
  }

  getProductCardStyleVars(): Record<string, string> {
    const styling = this.chatbot?.frontEndOptions?.styling as any;
    const colorOr = (value: unknown, fallback: string): string => {
      const trimmed = `${value ?? ''}`.trim();
      return trimmed === 'transparent' || /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(trimmed) ? trimmed : fallback;
    };
    const lengthOr = (value: unknown, fallback: string): string => {
      const trimmed = `${value ?? ''}`.trim();
      return /^(0|[0-9]+(\.[0-9]+)?(px|rem|em|%))$/.test(trimmed) ? trimmed : fallback;
    };
    const paddingOr = (value: unknown, fallback: string): string => {
      const trimmed = `${value ?? ''}`.trim();
      if (!trimmed) return fallback;
      const parts = trimmed.split(/\s+/);
      return parts.length <= 4 && parts.every(part => /^(0|[0-9]+(\.[0-9]+)?(px|rem|em|%))$/.test(part))
        ? trimmed
        : fallback;
    };
    const weightOr = (value: unknown, fallback: string): string => {
      const trimmed = `${value ?? ''}`.trim();
      return /^(normal|bold|[1-9]00)$/.test(trimmed) ? trimmed : fallback;
    };
    const ratioOr = (value: unknown, fallback: string): string => {
      const trimmed = `${value ?? ''}`.trim();
      const match = trimmed.match(/^([0-9]+(?:\.[0-9]+)?)\s*[:/]\s*([0-9]+(?:\.[0-9]+)?)$/);
      return match ? `${match[1]} / ${match[2]}` : fallback;
    };
    const currentPriceColor = colorOr(styling?.cardPriceCurrentColor, '#b42318');
    const saleCurrentPriceColor = colorOr(styling?.cardPriceSaleCurrentColor, currentPriceColor);

    return {
      '--bo-product-card-max-width': lengthOr(styling?.cardMaxWidth, '273px'),
      '--bo-product-card-background': colorOr(styling?.cardBackgroundColor, 'transparent'),
      '--bo-product-card-border-color': colorOr(styling?.cardBorderColor, 'transparent'),
      '--bo-product-card-padding': paddingOr(styling?.cardPadding, '1rem 0 0'),
      '--bo-product-card-radius': lengthOr(styling?.cardBorderRadius, '8px'),
      '--bo-product-card-image-ratio': ratioOr(styling?.cardImageAspectRatio, '3 / 4'),
      '--bo-product-card-image-background': colorOr(styling?.cardImageBackgroundColor, '#f3f4f6'),
      '--bo-product-card-title-size': lengthOr(styling?.cardTitleFontSize, '19px'),
      '--bo-product-card-title-weight': weightOr(styling?.cardTitleFontWeight, '600'),
      '--bo-product-card-description-size': lengthOr(styling?.cardDescriptionFontSize, '15px'),
      '--bo-product-card-price-size': lengthOr(styling?.cardPriceFontSize, '17px'),
      '--bo-product-card-price-weight': weightOr(styling?.cardPriceFontWeight, '700'),
      '--bo-product-card-current-price-color': currentPriceColor,
      '--bo-product-card-sale-current-price-color': saleCurrentPriceColor,
      '--bo-product-card-original-price-color': colorOr(styling?.cardPriceOriginalColor, '#111827'),
      '--bo-product-card-original-price-size': lengthOr(styling?.cardPriceOriginalFontSize, '15px'),
      '--bo-product-card-button-bg': colorOr(styling?.cardBtnBgColor, '#1f2937'),
      '--bo-product-card-button-bg-hover': colorOr(styling?.cardBtnBgColorHover, styling?.cardBtnBgColor || '#1f2937'),
      '--bo-product-card-button-color': colorOr(styling?.cardBtnColor, '#ffffff'),
      '--bo-product-card-button-color-hover': colorOr(styling?.cardBtnColorHover, styling?.cardBtnColor || '#ffffff'),
      '--bo-product-card-button-border': colorOr(styling?.cardBtnBorderColor, styling?.cardBtnBgColor || '#1f2937'),
      '--bo-product-card-button-border-hover': colorOr(styling?.cardBtnBorderColorHover, styling?.cardBtnBorderColor || styling?.cardBtnBgColor || '#1f2937')
    };
  }

  shouldShowProductCardDescription(): boolean {
    return this.chatbot?.frontEndOptions?.styling?.cardDescriptionVisible !== false;
  }

  shouldShowProductCardPrice(): boolean {
    return this.chatbot?.frontEndOptions?.styling?.cardPriceVisible !== false;
  }

  isCarouselGroup(group: ConversationProductCardsSegment): boolean {
    return group.presentation === 'vertical-carousel' || group.presentation === 'compact-carousel';
  }

  isVerticalListGroup(group: ConversationProductCardsSegment): boolean {
    return group.presentation === 'vertical-list';
  }

  isSalePrice(price: ProductCardPrice): boolean {
    return price.original.trim().length > 0;
  }

  getProductCardPrice(card: ConversationProductCardView): ProductCardPrice | null {
    const current = this.firstString(card.salePriceText, card.sale_price_text, card.salePrice, card.sale_price, card.priceText, card.price, card.priceValue);
    if (!current) return null;
    const original = this.firstString(card.regularPriceText, card.regular_price_text, card.originalPriceText, card.original_price_text, card.regularPrice, card.regular_price);
    const currency = card.currency || card.priceCurrency || card.priceSuffix;
    const currentDisplay = this.formatPrice(current, currency);
    const originalDisplay = original ? this.formatPrice(original, currency) : '';
    return {
      current: currentDisplay,
      original: originalDisplay && hasLowerComparablePrice(current, original, currency) ? originalDisplay : ''
    };
  }

  trackSegment(_: number, segment: ConversationTranscriptSegment): string {
    return segment.id;
  }

  trackCard(_: number, card: ConversationProductCardView): string {
    return card.id;
  }

  shouldShowProductCardImage(card: ConversationProductCardView): boolean {
    return Boolean(card.imageUrl) && !this.failedProductCardImages.has(card.id);
  }

  onProductCardImageError(card: ConversationProductCardView): void {
    this.failedProductCardImages.add(card.id);
  }

  trackAttachment(_: number, attachment: IAttachmentSummary): string {
    return `${attachment.fileName || 'attachment'}-${attachment.size || 0}`;
  }

  getAttachmentDisplayName(attachment: IAttachmentSummary, index: number): string {
    return getConversationAttachmentDisplayName(attachment, index);
  }

  getAttachmentMetaText(attachment: IAttachmentSummary): string {
    return getConversationAttachmentMetaText(attachment);
  }

  private firstString(...values: unknown[]): string {
    for (const value of values) {
      const text = `${value ?? ''}`.trim();
      if (text.length > 0) return text;
    }
    return '';
  }

  private formatPrice(value: string, currency?: string): string {
    const trimmed = `${value ?? ''}`.trim();
    const suffix = `${currency ?? ''}`.trim();
    if (!trimmed || !suffix || trimmed.toLowerCase().includes(suffix.toLowerCase())) return trimmed;
    return `${trimmed} ${suffix}`;
  }
}
